import { useEffect } from 'react';
import { StyleSheet, TextStyle, ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withSpring,
} from 'react-native-reanimated';

import colors from 'config/colors';
import { DEFAULT_SPRING_CONFIG } from 'config/constants';
import { useAppSelector } from 'store/hooks';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { useCountDownFromNowLabel } from 'util/hooks';
import { Text } from 'components';

import type { StyleProp } from 'react-native';

type Props = {
  latestPickupTime?: string;
  style?: StyleProp<ViewStyle>;
  textStyle?: TextStyle;
};

export const CountDownLabelComponent = ({
  latestPickupTime,
  style,
  textStyle,
}: Props) => {
  const isBlinkingEnabled = useAppSelector(
    SettingSelectors.selectCountDownTimerBlinkingEnabled,
  );
  const threshold = useAppSelector(
    SettingSelectors.selectCountDownTimerBlinkingThreshold,
  );

  const { label, isBlinking } = useCountDownFromNowLabel(
    latestPickupTime,
    threshold,
    isBlinkingEnabled,
  );

  const sharedAnimatedValue = useSharedValue(colors.gray);

  useEffect(() => {
    if (isBlinking) {
      sharedAnimatedValue.value = withRepeat(
        withSequence(
          withSpring(colors.lightRed, DEFAULT_SPRING_CONFIG),
          withSpring(colors.gray, DEFAULT_SPRING_CONFIG),
        ),
        -1,
        true,
      );
    } else {
      sharedAnimatedValue.value = withSpring(
        colors.gray,
        DEFAULT_SPRING_CONFIG,
      );
    }
  }, [isBlinking]);

  const animatedContainerStyle = useAnimatedStyle(() => {
    const backgroundColor = sharedAnimatedValue.value;

    return {
      backgroundColor,
    };
  }, []);

  return (
    <Animated.View style={[animatedContainerStyle, styles.countDown, style]}>
      <Text color="white" size={12} numberOfLines={1} style={textStyle}>
        {label}
      </Text>
    </Animated.View>
  );
};

export const CountDownLabel = (props: Props) => {
  const isCountDownTimerEnabled = useAppSelector(
    SettingSelectors.selectCountDownTimerEnable,
  );

  const featureFlags = useAppSelector(UserSelectors.selectFeatureFlags);

  const {
    kds_enabled_traffic_light_system: isTrafficLightSystemEnabled,
    kds_enabled: isKdsEnabled,
  } = featureFlags;

  if (
    !isCountDownTimerEnabled ||
    !isTrafficLightSystemEnabled ||
    !isKdsEnabled
  ) {
    return null;
  }

  return <CountDownLabelComponent {...props} />;
};

const styles = StyleSheet.create({
  countDown: {
    marginTop: 5,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
    width: 50,
    height: 30,
    padding: 2,
  },
});
